
import { getProfile, getVersion } from '@/api';
import SideMenu from '@/components/sideMenu/SideMenu.vue';
import ButtonToTop from '@/components/UI/buttonToTop/ButtonToTop.vue';
import store from '@/store';
import { User } from '@/models/main';
import { AuthData } from '@/models/auth';
import { Ability, AbilityBuilder } from '@casl/ability';
import { Component, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { notificationErrorText } from '@/utils/PortalConstants';

import { compareVersions } from '@/utils/InputProcessingUtilities';
import { timeZoneDefault } from '@/utils/PortalConstants';
import { sendWarning } from '@/helpers/sentry';

@Component({
  components: {
    SideMenu,
    ButtonToTop,
  },
  computed: {
    ...mapState('moduleAuthData', ['authData']),
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class DefaultLayout extends Vue {
  private loading = false;
  public showTopButton = false;
  private authData: AuthData;
  private user!: User;
  public isUserDataDispatchedToStore = false;

  private async beforeCreate() {
    let userData: User = {
      staff: false,
      username: '',
      customer: '',
      customerCode: '',
      avatar: '',
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      state: [],
      timezone: timeZoneDefault,
      ref1_prompt: '',
      ref2_prompt: '',
      ref3_prompt: '',
      zammad_groups: [],
      zammad_id: 0,
      zammad_login: null,
      show_new_ui_notice: false,
    };
    await getProfile()
      .then((res: User) => {
        userData = res;
        store.dispatch('moduleSocket/socketConnect');
        store.dispatch('moduleStaticData/requestAllStaticData');
      })
      .catch(() => {
        store.dispatch('moduleAuthData/logOutUser');
        store.dispatch('setLoginError', 'Having problems getting a profile');
      })
      .finally(() => {
        store.dispatch('moduleUser/setUser', userData).finally(() => {
          this.isUserDataDispatchedToStore = true;
        });
      });
  }

  private async created(): Promise<void> {
    this.loading = true;

    // portal version check timer
    const portalVersionInterval = setInterval(() => {
      // getting version from the file from the server
      getVersion()
        .then(res => {
          const compareAppVersionsResult = compareVersions(res, process.env.VERSION || '1.0.0');
          // reloading the page if current version (from the .env file) is smaller
          // than version from the file from the server
          if (compareAppVersionsResult >= 1) {
            // showing notification
            this.$notify({
              group: 'network',
              title: 'The portal version is outdated',
              text: 'The page will be reloaded in 5 seconds',
              type: 'warn',
            });
            // reloading page after 5 seconds
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }
        })
        .catch(error => {
          // On error:
          // sending the warning to Sentry
          sendWarning(error, 'Get portal version error');
          // clearing interval
          clearInterval(portalVersionInterval);
          // notification for user
          this.$notify({
            group: 'network',
            title: 'The portal version is outdated',
            text: 'Refresh this page please',
            type: 'warn',
          });
        });
    }, 60 * 60 * 1000); // one hour

    // send profile request only if user have access
    if (store.getters['moduleAuthData/authData'].access !== '') {
      if (this.authData.permissions) {
        const { can, rules } = new AbilityBuilder(Ability);
        this.authData.permissions.forEach(permission => {
          const subject = permission.subject;
          permission.actions.forEach(action => {
            can(action, subject);
          });
        });
        this.$ability.update(rules);
      }
      store.dispatch('moduleUser/fetchUser', this.$ability.can('staff', 'Job'));

      window.addEventListener('scroll', this.handleScroll);
    } else {
      this.loading = false;
    }
  }
  private handleScroll(): void {
    window.pageYOffset > 0 ? (this.showTopButton = true) : (this.showTopButton = false);
  }

  private beforeDestroy(): void {
    // Disconnect web socket on Log Out
    store.dispatch('moduleSocket/socketDisconnect');
  }

  private destroyed(): void {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
